
import { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { CourseRatingCard } from "./review-management/CourseRatingCard";
import { ReviewSettings } from "./review-management/ReviewSettings";
import { ReviewList } from "./review-management/ReviewList";
import { BulkActionBar } from "./review-management/BulkActionBar";
import { ReviewType } from "./review-management/types";
import { useReviewsManagement } from "@/hooks/useReviewsManagement";

interface ReviewsManagementProps {
  courseId: string;
}

const ReviewsManagement = ({ courseId }: ReviewsManagementProps) => {
  const queryClient = useQueryClient();
  const [showRatings, setShowRatings] = useState(true);
  const [showFeaturedReviews, setShowFeaturedReviews] = useState(true);
  
  const {
    reviewState,
    isLoading,
    selectedReviews,
    deleteReviewId,
    accessToken,
    setSelectedReviews,
    setDeleteReviewId,
    handlePublicVisibilityToggle,
    handleFeaturedToggle,
    handleBulkFeature,
    deleteReviewMutation
  } = useReviewsManagement(courseId);

  const { data: course } = useQuery({
    queryKey: ['course', courseId],
    queryFn: async () => {
      console.log("[CourseQuery] Fetching course settings:", courseId);
      const { data, error } = await supabase
        .from('courses')
        .select('show_ratings, show_featured_reviews')
        .eq('id', courseId)
        .single();

      if (error) {
        console.error("[CourseQuery] Error:", error);
        throw error;
      }
      console.log("[CourseQuery] Success:", data);
      return data;
    }
  });

  useEffect(() => {
    if (course) {
      setShowRatings(course.show_ratings ?? true);
      setShowFeaturedReviews(course.show_featured_reviews ?? true);
    }
  }, [course]);

  const { data: averageRating } = useQuery({
    queryKey: ['course-average-rating', courseId],
    queryFn: async () => {
      try {
        console.log("[RatingQuery] Fetching average rating for course:", courseId);
        
        if (!accessToken) {
          console.warn("[RatingQuery] No access token available");
          return 0;
        }
        
        const response = await fetch(`https://hbgmfqtsdizjedoanieo.supabase.co/functions/v1/course-reviews`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            path: 'get_average_rating',
            params: { course_id: courseId }
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error("[RatingQuery] HTTP Error:", response.status, errorText);
          throw new Error(`Error ${response.status}: ${errorText}`);
        }
        
        const data = await response.json();
        console.log("[RatingQuery] Success, average rating:", data);
        return data || 0;
      } catch (error) {
        console.error("[RatingQuery] Exception:", error);
        return 0;
      }
    },
    enabled: !!accessToken
  });

  const { data: reviewCount } = useQuery({
    queryKey: ['course-review-count', courseId],
    queryFn: async () => {
      try {
        console.log("[ReviewCountQuery] Counting reviews for course:", courseId);
        // Use .select() with .limit() instead of count/head
        const { data, error } = await supabase
          .from('course_reviews')
          .select('id', { count: 'exact', head: false })
          .eq('course_id', courseId)
          .limit(1);

        if (error) {
          console.error("[ReviewCountQuery] Error:", error);
          throw error;
        }
        console.log("[ReviewCountQuery] Success, count:", data?.length);
        return data?.length || 0;
      } catch (error) {
        console.error("[ReviewCountQuery] Exception:", error);
        return 0;
      }
    }
  });

  const toggleRatingsVisibility = async (checked: boolean) => {
    try {
      console.log("[ToggleRatings] Setting visibility to:", checked);
      const { error } = await supabase
        .from('courses')
        .update({ show_ratings: checked })
        .eq('id', courseId);
      
      if (error) {
        console.error("[ToggleRatings] Error:", error);
        throw error;
      }
      
      console.log("[ToggleRatings] Success");
      setShowRatings(checked);
      toast.success(`Ratings ${checked ? 'displayed' : 'hidden'}`);
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    } catch (error) {
      console.error("[ToggleRatings] Exception:", error);
      toast.error('Failed to update ratings visibility');
    }
  };

  const toggleFeaturedReviewsVisibility = async (checked: boolean) => {
    try {
      console.log("[ToggleFeaturedReviews] Setting visibility to:", checked);
      const { error } = await supabase
        .from('courses')
        .update({ show_featured_reviews: checked })
        .eq('id', courseId);
      
      if (error) {
        console.error("[ToggleFeaturedReviews] Error:", error);
        throw error;
      }
      
      console.log("[ToggleFeaturedReviews] Success");
      setShowFeaturedReviews(checked);
      toast.success(`Featured reviews ${checked ? 'displayed' : 'hidden'}`);
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    } catch (error) {
      console.error("[ToggleFeaturedReviews] Exception:", error);
      toast.error('Failed to update featured reviews visibility');
    }
  };

  const handleDeleteReview = async () => {
    if (!deleteReviewId) {
      console.warn("[HandleDeleteReview] Called with no review ID");
      return;
    }
    
    console.log("[HandleDeleteReview] Proceeding with deletion for review:", deleteReviewId);
    deleteReviewMutation.mutate(deleteReviewId);
  };

  const handleCloseDeleteDialog = () => {
    console.log("[CloseDeleteDialog] Closing delete confirmation dialog");
    setDeleteReviewId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading reviews...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <CourseRatingCard 
          averageRating={averageRating || 0} 
          totalReviews={reviewCount || 0} 
        />
        <ReviewSettings 
          showRatings={showRatings}
          showFeaturedReviews={showFeaturedReviews}
          onToggleRatings={toggleRatingsVisibility}
          onToggleFeaturedReviews={toggleFeaturedReviewsVisibility}
        />
      </div>

      {selectedReviews.length > 0 && (
        <BulkActionBar 
          selectedCount={selectedReviews.length}
          onFeatureSelected={handleBulkFeature}
        />
      )}
      
      <ReviewList 
        reviews={reviewState}
        selectedReviews={selectedReviews}
        onSelectReview={(reviewId, checked) => {
          if (checked) {
            setSelectedReviews(prev => [...prev, reviewId]);
          } else {
            setSelectedReviews(prev => prev.filter(id => id !== reviewId));
          }
        }}
        onToggleVisibility={handlePublicVisibilityToggle}
        onToggleFeatured={handleFeaturedToggle}
        onDeleteRequest={setDeleteReviewId}
      />

      <AlertDialog 
        open={!!deleteReviewId} 
        onOpenChange={handleCloseDeleteDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Review</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this review? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteReview}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReviewsManagement;
